"use client";

// ── The problem list, with numbers on it (`EX-PUB15`) ────────────────────────
// The founder's question, looking at a rehearsal round: "which of these is the
// easy one, and what have I already done?" The list answered neither, so it was
// a letter, a title and nothing else.
//
// **Both numbers come from the scoreboard's own fold, and that is the whole
// design.** `GET /v1/contests/:slug` has no cache, so counting here would read
// every submission in the contest on every page load (`EX-CAP01` measured that
// read as the API droplet's ceiling); and a second aggregate would count staff
// and disqualified contestants, who `buildStandingsEntries` drops, so the list
// would disagree with the scoreboard on the same screen. Instead this reads the
// standings query the standings tab already makes: same key, one request, one
// cache entry, and under a freeze the counts are capped exactly as the table is.
//
// `EX-PUB22` drew it to the "Judge pages" proposal: the counts as a bar, who
// solved each problem first (a flag the same fold already sets), the other
// language's title under the title, and once the round is over a Practice
// link of its own beside the row's link to the round's copy.

import Link from "next/link";
import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { ArrowRight, Star } from "lucide-react";

import { cn } from "@/lib/utils";
import { RunTwiceTag } from "@/features/judge/components/RunTwiceTag";
import { InteractiveTag } from "@/features/judge/components/InteractiveTag";
import { FunctionTag } from "@/features/judge/components/workspace/FunctionSignatureCard";
import { formatNumber } from "@/lib/format-datetime";
import { useContestStandings, useMyContestSubmissions } from "../hooks";
import { formatContestMinute } from "../lib/format";
import { buildLetterStates, letterStateOf, type ContestLetterState } from "../lib/letter-state";
import type { ContestStandingsResponse, PublicContestDetail } from "../types";

const LETTER_CLASS: Readonly<Record<ContestLetterState, string>> = {
  solved: "bg-emerald-600 text-white",
  attempted: "bg-red-50 text-red-700",
  untouched: "bg-neutral-900 text-white",
};

const MINE_CLASS: Readonly<Record<ContestLetterState, string>> = {
  solved: "bg-emerald-50 text-emerald-700",
  attempted: "bg-red-50 text-red-700",
  untouched: "text-neutral-500",
};

/**
 * `EX-PUB22`: who solved each problem first, found on the rows the standings
 * read already holds. The fold marks the cell (`firstSolve`), so nothing here
 * decides who was first. A first solver ranked below the page in hand is not
 * found and nothing is printed for that problem, which is quieter than a guess
 * and never wrong.
 */
export function firstSolversOf(
  standings: Pick<ContestStandingsResponse, "rows"> | undefined,
): Map<string, { name: string; minute: number | null }> {
  const found = new Map<string, { name: string; minute: number | null }>();
  for (const row of standings?.rows ?? []) {
    if (!row.name) continue;
    for (const cell of row.cells) {
      if (cell.firstSolve && !found.has(cell.letter)) {
        found.set(cell.letter, { name: row.name, minute: cell.solvedAtMinute });
      }
    }
  }
  return found;
}

export function ContestProblemList({
  contest,
  locale,
}: {
  contest: PublicContestDetail;
  locale: string;
}) {
  const t = useTranslations("contest.page");
  const problems = contest.problems ?? [];
  const hasEnded = contest.phase === "ended" || contest.phase === "unfrozen";

  /**
   * The scoreboard answers 403 until the window opens, so the query is not made
   * before then. A moderator reads it early, which is the same gate the server
   * applies rather than a second opinion about it.
   */
  const standingsReadable =
    contest.viewer.canModerate || (contest.phase !== "draft" && contest.phase !== "scheduled");
  const standings = useContestStandings(
    contest.slug,
    { view: "auto", page: 1 },
    standingsReadable && problems.length > 0,
  );
  // `EX-VRT01`: a virtual taker's own state per letter is their try's.
  const virtualTry = Boolean(contest.viewer.virtual?.attempt);
  const mySubmissions = useMyContestSubmissions(contest.slug, contest.viewer.isRegistered || virtualTry, {}, virtualTry);

  const columns = useMemo(
    () => new Map((standings.data?.problems ?? []).map((problem) => [problem.letter, problem])),
    [standings.data],
  );
  const firstSolvers = useMemo(() => firstSolversOf(standings.data), [standings.data]);
  const letterStates = useMemo(
    () => buildLetterStates(mySubmissions.data?.items ?? []),
    [mySubmissions.data],
  );

  if (problems.length === 0) {
    return (
      <p className="rounded-xl border border-neutral-200 bg-white p-8 text-center text-sm text-neutral-500">
        {t("noProblems")}
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
      {problems.map((problem) => {
        const title =
          locale === "bn" && problem.title_bn ? problem.title_bn : problem.title_en;
        // The library's rule (`EX-PUB21`): the other language under the title.
        const otherTitle = problem.title_bn
          ? locale === "bn"
            ? problem.title_en
            : problem.title_bn
          : null;
        const column = columns.get(problem.letter);
        const state = letterStateOf(letterStates, problem.letter);
        const first = firstSolvers.get(problem.letter);
        const solved = column?.solvedCount;
        const tried = column?.attemptedCount ?? solved;
        const problemHref = `/${locale}/contests/${contest.slug}/problems/${problem.letter}`;
        const practiceHref =
          hasEnded && problem.practiceSlug ? `/${locale}/judge/${problem.practiceSlug}` : null;

        return (
          <div
            key={problem.letter}
            className="group relative grid grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-x-4 gap-y-3 border-b border-neutral-100 px-4 py-4 transition-colors duration-200 last:border-b-0 hover:bg-neutral-50 sm:px-5 lg:grid-cols-[36px_minmax(0,1fr)_minmax(0,240px)_minmax(0,210px)_auto] lg:gap-x-6 lg:px-6"
          >
            <span
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-[9px] text-[15px] font-bold",
                LETTER_CLASS[state],
              )}
            >
              {problem.letter}
            </span>

            <span className="min-w-0">
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {/* The row's link, stretched over the row; Practice sits above it. */}
                <Link
                  href={problemHref}
                  className="rounded-sm text-base font-bold text-neutral-900 outline-none transition-colors duration-200 after:absolute after:inset-0 group-hover:text-primary-ink focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-primary"
                >
                  {title ?? "-"}
                </Link>
                {/* `EX-RTW03`: said on the list, before a contestant opens it. */}
                {problem.kind === "run_twice" ? <RunTwiceTag size="sm" /> : null}
                {problem.kind === "interactive" ? <InteractiveTag size="sm" /> : null}
                {problem.answer === "function" ? <FunctionTag size="sm" /> : null}
                {(contest.viewer.isRegistered || virtualTry) && state !== "untouched" ? (
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                      MINE_CLASS[state],
                    )}
                  >
                    {t(state === "solved" ? "problemMineSolved" : "problemMineAttempted")}
                  </span>
                ) : null}
              </span>
              {otherTitle ? (
                <span className="mt-0.5 block truncate text-[13px] text-neutral-400">{otherTitle}</span>
              ) : null}
            </span>

            <span className="relative z-10 col-start-3 row-start-1 flex justify-end lg:col-start-5">
              {practiceHref ? (
                <Link
                  href={practiceHref}
                  className="inline-flex h-[34px] items-center gap-1.5 whitespace-nowrap rounded-[9px] border border-neutral-200 bg-white px-3 text-[13px] font-semibold text-neutral-900 transition-colors duration-200 hover:border-primary hover:text-primary-ink"
                >
                  {t("practiceProblem")}
                  <span className="sr-only">: {title}</span>
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                </Link>
              ) : (
                /*
                  A real link, not a decoration: this wrapper is `z-10` so that
                  Practice sits above the stretched row link, which means the
                  row link's pseudo-element does not reach under this button
                  either. A span here swallowed the click and opened nothing.
                  Hidden from the tab order and screen readers because the
                  title link already names the same destination.
                */
                <Link
                  href={problemHref}
                  tabIndex={-1}
                  aria-hidden
                  className="inline-flex h-[34px] items-center gap-1.5 whitespace-nowrap rounded-[9px] border border-neutral-200 px-3 text-[13px] font-semibold text-neutral-900 transition-colors duration-200 group-hover:border-primary group-hover:text-primary-ink"
                >
                  {t("openProblem")}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              )}
            </span>

            {/*
              How much of the field has this one. Absent until the window opens,
              and on a frozen board it is the frozen number: the same value the
              column header shows, because it is the same fold.
            */}
            {solved !== undefined && tried !== undefined ? (
              <span
                title={t("problemStatsTitle", { solved, attempted: tried })}
                className="col-span-2 col-start-2 flex flex-col gap-1.5 text-[12.5px] text-neutral-500 lg:col-span-1 lg:col-start-3 lg:row-start-1"
              >
                <span>
                  {tried > 0
                    ? t.rich("solvedOfTried", {
                        b: (chunks) => <b className="font-semibold text-neutral-900">{chunks}</b>,
                        solved: formatNumber(solved, locale),
                        tried: formatNumber(tried, locale),
                      })
                    : t("nobodyTried")}
                </span>
                <span className="block h-1.5 overflow-hidden rounded-full bg-neutral-100">
                  <span
                    className="block h-full rounded-full bg-emerald-500"
                    style={{ width: `${tried > 0 ? Math.round((solved / tried) * 100) : 0}%` }}
                  />
                </span>
              </span>
            ) : null}

            {first ? (
              <span className="col-span-2 col-start-2 flex min-w-0 items-center gap-2 text-[13px] text-neutral-500 lg:col-span-1 lg:col-start-4 lg:row-start-1">
                <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                  <Star className="h-3 w-3" aria-hidden />
                </span>
                <span className="min-w-0 truncate">
                  {t.rich("firstSolver", {
                    b: (chunks) => <b className="font-medium text-neutral-900">{chunks}</b>,
                    name: first.name,
                    time: first.minute === null ? "" : formatContestMinute(first.minute),
                  })}
                </span>
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
